import { FeatureHubGuard } from "@/featureFlags";
import { IntegerInput } from "@/overlay/components/CommonParts/CustomInput";
import type { AdvancedThresholdDetails } from "@/overlay/components/CommonParts/Threshold/types";
import { InitDateField } from "@/overlay/components/LayerStackWindow/InitDateField";
import { ModelDropdownHavingParam } from "@/overlay/components/LayerStackWindow/LayerFormElements";
import { CalibratedToggle } from "@/overlay/components/LayerStackWindow/LayerFormElements/CalibratedToggle";
import { BasicOffsetControl } from "@/overlay/components/QuickAccessLabel";
import { BasicWeatherParameterForm } from "@/overlay/components/WeatherParameterForm/BasicWeatherParameterForm";
import { FormField } from "@/overlay/components/form-field";
import { type ConditionOptions, conditionValues } from "@/utility/conditionalExpressions";
import { classNames } from "@/utility/jsx";
import { t } from "@lingui/core/macro";
import { Aggregate } from "@mm/api.meteomatics.com";
import { parameterToString } from "@mm/weather-parameter-utils";
import { MmDropdown, MmTooltipWrapper } from "@mm/zeus-react";
import React from "react";
import styles from "./AdvancedThreshold.module.scss";

export type AdvancedThresholdElementProps = {
  advancedProps: AdvancedThresholdDetails;
  onPropsChange: (updatedProps: Partial<AdvancedThresholdDetails>) => void;
  isPoi?: boolean;
  onRemoveOption?: () => void;
};

export const AdvancedThresholdElement = (props: AdvancedThresholdElementProps) => {
  const { advancedProps, onPropsChange, isPoi = false } = props;
  return (
    <>
      <div className={styles.parameterSection}>
        {props.onRemoveOption && (
          <MmTooltipWrapper
            className={styles.removeButtonContainer}
            tail={true}
            position="left"
            tooltip-text={t`Delete Option`}
          >
            <button type={"button"} onClick={() => props.onRemoveOption?.()}>
              <span className={classNames("material-icons")}>delete_outline</span>
            </button>
          </MmTooltipWrapper>
        )}
        <BasicWeatherParameterForm
          parameterStr={advancedProps.parameter}
          showExactParameter={true}
          autofill={true}
          onSubmitParam={(newParam) => {
            onPropsChange({ parameter: parameterToString(newParam)?.formatted });
          }}
          parameterFormFieldProps={{ alignTop: true, background: false, slimPadding: true }}
        />
        <div className={styles.twoColumnRow}>
          <ModelDropdownHavingParam
            parameterStr={advancedProps.parameter}
            onChange={(newModel) => onPropsChange({ model: newModel })}
            formFieldProps={{ alignTop: true, background: false, slimPadding: true }}
            selectedModel={advancedProps.model}
          />
          {!isPoi && (
            <FormField label={t`Aggregate`} alignTop background={false} slimPadding>
              <MmDropdown
                items={Object.values(Aggregate).map((f) => ({ id: f, title: f.toUpperCase() }))}
                verticalAlignment="auto"
                activeId={advancedProps.aggregate ? [advancedProps.aggregate] : []}
                itemSelectedHandler={(selection) => onPropsChange({ aggregate: selection[0].id as Aggregate })}
              />
            </FormField>
          )}
        </div>
        <FormField label={t`Threshold Condition`} alignTop background={false} slimPadding noSemanticLinking>
          <div className={styles.conditionSection}>
            <MmDropdown
              items={conditionValues?.map((f) => ({ id: f.toString(), title: `${f.toUpperCase()}` }))}
              verticalAlignment="auto"
              activeId={advancedProps.condition ? [advancedProps.condition] : []}
              itemSelectedHandler={(selection) => onPropsChange({ condition: selection[0].id as ConditionOptions })}
            />
            <IntegerInput
              value={advancedProps.value}
              onChange={(newThresholdValue) => onPropsChange({ value: newThresholdValue })}
              step={advancedProps.parameter?.includes("dust_aod") ? 0.1 : 1}
              decimals={true}
              data-testid={"threshold-value"}
              fab
            />
          </div>
        </FormField>
        <FormField label={t`Time Offset`} alignTop background={false} slimPadding>
          <div className={styles.offsetSection}>
            <BasicOffsetControl
              onOffsetChange={(newOffset) => onPropsChange({ offsetMinutes: newOffset })}
              offsetMinutes={advancedProps.offsetMinutes}
            />
          </div>
        </FormField>
        {isPoi && (
          <>
            <CalibratedToggle
              value={!!advancedProps.calibrated}
              change={({ calibrated }) => onPropsChange({ calibrated })}
            />
            <FeatureHubGuard flag="api_url_suffix">
              <InitDateField
                label={t`API URL suffix`}
                defaultValue={advancedProps.apiUrlSuffix ?? ""}
                onChange={(apiUrlSuffix) => onPropsChange({ apiUrlSuffix })}
              />
            </FeatureHubGuard>
          </>
        )}
      </div>
    </>
  );
};
